import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { US_STATE_ABBREVIATIONS, isValidUsStateAbbrev, normalizeUsState } from '../utils/usStates';
import { getDeviceLatLng, DeviceLatLng } from '../utils/deviceLocation';
import { isGoogleMapsJsConfigured, loadGoogleMapsJs } from '../utils/googleMapsLoader';
import { api } from '../services/api';

export interface AddressValue {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface AddressValidateResult {
  available: boolean;
  verdict: 'accept' | 'confirm' | 'fix' | 'unavailable';
  message?: string | null;
  address: Partial<AddressValue> & {
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  raw_formatted?: string | null;
}

export interface AddressFieldsProps {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  disabled?: boolean;
  required?: boolean;
  /** Optional Grid item(s) rendered after City (e.g. County). */
  afterCity?: React.ReactNode;
  showMapPicker?: boolean;
  showCurrentLocation?: boolean;
  showValidate?: boolean;
  /** Soft validation hint shown below fields. */
  helperText?: string;
  /** After a failed save, outline empty required fields in red. */
  showErrors?: boolean;
}

function toUpper(value: string): string {
  return value.toUpperCase();
}

function placeToAddress(place: any): Partial<AddressValue> {
  const comps: any[] = place?.address_components || [];
  const findComp = (...types: string[]) => {
    for (const type of types) {
      const match = comps.find(component => (component.types || []).includes(type));
      if (match) return match;
    }
    return undefined;
  };
  const get = (...types: string[]) => {
    const match = findComp(...types);
    return match ? match.short_name || match.long_name || '' : '';
  };
  const getLong = (...types: string[]) => {
    const match = findComp(...types);
    return match ? match.long_name || match.short_name || '' : '';
  };
  const streetNum = get('street_number');
  const route = getLong('route');
  const line1 = `${streetNum} ${route}`.trim();
  const lat = place?.geometry?.location?.lat?.();
  const lng = place?.geometry?.location?.lng?.();
  return {
    address_line1: toUpper(line1),
    address_line2: toUpper(get('subpremise')),
    city: toUpper(getLong('locality', 'sublocality', 'postal_town', 'neighborhood')),
    state: normalizeUsState(get('administrative_area_level_1')),
    zip_code: toUpper(get('postal_code')),
    latitude: typeof lat === 'number' ? lat : undefined,
    longitude: typeof lng === 'number' ? lng : undefined,
  };
}

function mergeAddress(current: AddressValue, patch: Partial<AddressValue>): AddressValue {
  return {
    address_line1: patch.address_line1 != null ? toUpper(String(patch.address_line1)) : current.address_line1,
    address_line2: patch.address_line2 != null ? toUpper(String(patch.address_line2)) : current.address_line2,
    city: patch.city != null ? toUpper(String(patch.city)) : current.city,
    state: patch.state != null ? normalizeUsState(String(patch.state)) : current.state,
    zip_code: patch.zip_code != null ? toUpper(String(patch.zip_code)) : current.zip_code,
    latitude: patch.latitude !== undefined ? patch.latitude : current.latitude,
    longitude: patch.longitude !== undefined ? patch.longitude : current.longitude,
  };
}

type LatLng = DeviceLatLng;

type PlaceSuggestion = {
  description: string;
  place_id: string;
};

/** Last-resort map center only — never treat this as the user's location. */
const FALLBACK_MAP_CENTER: LatLng = { lat: 39.9612, lng: -82.9988 }; // Columbus, OH

const AddressFields: React.FC<AddressFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  afterCity,
  showMapPicker = true,
  showCurrentLocation = true,
  showValidate = true,
  helperText,
  showErrors = false,
}) => {
  const line1Ref = useRef<HTMLInputElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const suggestTimerRef = useRef<number | null>(null);
  const selectionTicketRef = useRef(0);
  const queryTicketRef = useRef(0);
  onChangeRef.current = onChange;
  valueRef.current = value;

  const mapsConfigured = isGoogleMapsJsConfigured();
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapBusy, setMapBusy] = useState(false);
  const [validating, setValidating] = useState(false);
  const [locating, setLocating] = useState(false);
  const [mapLocating, setMapLocating] = useState(false);
  const [mapGeoHint, setMapGeoHint] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [statusSeverity, setStatusSeverity] = useState<'info' | 'success' | 'warning' | 'error'>('info');
  const [pendingPin, setPendingPin] = useState<LatLng | null>(null);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  useEffect(() => {
    if (!mapsConfigured || disabled) return;
    let cancelled = false;
    loadGoogleMapsJs()
      .then(() => {
        if (!cancelled) {
          setMapsReady(true);
          setMapsError(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setMapsReady(false);
          setMapsError(err?.message || 'Map features unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mapsConfigured, disabled]);

  useEffect(() => {
    if (!mapsReady || !window.google?.maps?.places) return;
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
  }, [mapsReady]);

  useEffect(() => {
    return () => {
      if (suggestTimerRef.current != null) window.clearTimeout(suggestTimerRef.current);
    };
  }, []);

  const closeSuggestions = () => {
    setSuggestionsOpen(false);
    setActiveSuggestion(-1);
  };

  const querySuggestions = (text: string) => {
    const service = autocompleteServiceRef.current;
    const query = text.trim();
    const ticket = ++queryTicketRef.current;
    if (!service || query.length < 3) {
      setSuggestions([]);
      closeSuggestions();
      return;
    }
    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      (predictions: any[] | null, status: string) => {
        if (ticket !== queryTicketRef.current) return;
        if (line1Ref.current && document.activeElement !== line1Ref.current) return;
        if (status !== 'OK' || !predictions?.length) {
          setSuggestions([]);
          closeSuggestions();
          return;
        }
        setSuggestions(
          predictions.map(prediction => ({
            description: prediction.description,
            place_id: prediction.place_id,
          }))
        );
        setSuggestionsOpen(true);
        setActiveSuggestion(-1);
      }
    );
  };

  const handleLine1Change = (text: string) => {
    selectionTicketRef.current += 1;
    onChangeRef.current(mergeAddress(valueRef.current, { address_line1: text }));
    if (suggestTimerRef.current != null) window.clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = window.setTimeout(() => querySuggestions(text), 250);
  };

  const selectSuggestion = (placeId: string) => {
    const service = placesServiceRef.current;
    if (!service) return;
    const ticket = ++selectionTicketRef.current;
    closeSuggestions();
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'geometry', 'formatted_address'],
      },
      (place: any, status: string) => {
        if (ticket !== selectionTicketRef.current) return;
        if (status !== 'OK' || !place) {
          setStatusMsg('Could not load that address. Enter it manually or try another suggestion.');
          setStatusSeverity('warning');
          return;
        }
        onChangeRef.current(mergeAddress(valueRef.current, placeToAddress(place)));
        setStatusMsg('Address filled from Google Places. You can still edit fields.');
        setStatusSeverity('success');
      }
    );
  };

  const handleLine1KeyDown = (event: React.KeyboardEvent) => {
    if (!suggestionsOpen || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion(index => Math.min(suggestions.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion(index => Math.max(0, index - 1));
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeSuggestion].place_id);
    } else if (event.key === 'Escape') {
      closeSuggestions();
    }
  };

  const initMap = useCallback((center: LatLng) => {
    if (!mapContainerRef.current || !window.google?.maps) return;

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center,
        zoom: 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      markerRef.current = new window.google.maps.Marker({
        map: mapRef.current,
        position: center,
        draggable: true,
      });
      const setPin = (latLng: { lat: () => number; lng: () => number } | LatLng) => {
        const lat = typeof (latLng as any).lat === 'function' ? (latLng as any).lat() : (latLng as any).lat;
        const lng = typeof (latLng as any).lng === 'function' ? (latLng as any).lng() : (latLng as any).lng;
        setPendingPin({ lat, lng });
        setMapGeoHint(null);
        markerRef.current.setVisible(true);
        markerRef.current.setPosition({ lat, lng });
      };
      mapRef.current.addListener('click', (e: any) => {
        if (e.latLng) setPin(e.latLng);
      });
      markerRef.current.addListener('dragend', (e: any) => {
        if (e.latLng) setPin(e.latLng);
      });
    } else {
      mapRef.current.setCenter(center);
      markerRef.current.setPosition(center);
    }
  }, []);

  useEffect(() => {
    if (!mapOpen || !mapsReady) return;
    // Reset map instance when dialog opens so container remounts cleanly
    mapRef.current = null;
    markerRef.current = null;
    setPendingPin(null);
    setMapGeoHint(null);
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const hasSaved =
        value.latitude != null &&
        value.longitude != null &&
        Number.isFinite(Number(value.latitude)) &&
        Number.isFinite(Number(value.longitude));
      let center: LatLng = hasSaved
        ? { lat: Number(value.latitude), lng: Number(value.longitude) }
        : FALLBACK_MAP_CENTER;
      let fromDevice = false;
      if (!hasSaved) {
        setMapLocating(true);
        try {
          center = await getDeviceLatLng();
          fromDevice = true;
        } catch {
          // Browser GPS unavailable — do not treat the regional fallback as "current location"
        } finally {
          if (!cancelled) setMapLocating(false);
        }
      }
      if (cancelled) return;
      initMap(center);
      if (fromDevice || hasSaved) {
        setPendingPin(center);
        if (markerRef.current) markerRef.current.setVisible(true);
        if (fromDevice) setMapGeoHint('Centered on your device location.');
      } else {
        setPendingPin(null);
        if (markerRef.current) markerRef.current.setVisible(false);
        setMapGeoHint('Could not get your device location. Click the map to drop a pin.');
      }
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [mapOpen, mapsReady, initMap, value.latitude, value.longitude]);

  const fillAddressFromLatLng = async (
    coords: LatLng,
    successMsg: string
  ): Promise<void> => {
    const result = await api.addresses.reverseGeocode({
      latitude: coords.lat,
      longitude: coords.lng,
    });
    if (!result?.available) {
      onChange(
        mergeAddress(value, {
          latitude: coords.lat,
          longitude: coords.lng,
        })
      );
      setStatusMsg(result?.message || 'Could not resolve address; coordinates saved. Enter address manually.');
      setStatusSeverity('warning');
      return;
    }
    onChange(
      mergeAddress(value, {
        address_line1: result.address?.address_line1 || '',
        address_line2: result.address?.address_line2 || '',
        city: result.address?.city || '',
        state: result.address?.state || '',
        zip_code: result.address?.zip_code || '',
        latitude: result.address?.latitude ?? coords.lat,
        longitude: result.address?.longitude ?? coords.lng,
      })
    );
    setStatusMsg(successMsg);
    setStatusSeverity('success');
  };

  const handleUsePin = async () => {
    if (!pendingPin) return;
    setMapBusy(true);
    setStatusMsg(null);
    try {
      await fillAddressFromLatLng(pendingPin, 'Address filled from map location.');
      setMapOpen(false);
    } catch (e: any) {
      onChange(mergeAddress(value, { latitude: pendingPin.lat, longitude: pendingPin.lng }));
      setStatusMsg(e?.message || 'Geocoding failed. Enter address manually.');
      setStatusSeverity('warning');
      setMapOpen(false);
    } finally {
      setMapBusy(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    setLocating(true);
    setStatusMsg(null);
    try {
      const coords = await getDeviceLatLng();
      await fillAddressFromLatLng(coords, 'Address filled from your device location.');
    } catch (e: any) {
      setStatusMsg(e?.message || 'Could not determine your device location. Pick on the map instead.');
      setStatusSeverity('warning');
    } finally {
      setLocating(false);
    }
  };

  const handleRecenterMapOnDevice = async () => {
    setMapLocating(true);
    setMapGeoHint(null);
    try {
      const coords = await getDeviceLatLng();
      initMap(coords);
      if (markerRef.current) {
        markerRef.current.setVisible(true);
        markerRef.current.setPosition(coords);
      }
      if (mapRef.current) mapRef.current.setCenter(coords);
      setPendingPin(coords);
      setMapGeoHint('Centered on your device location.');
    } catch (e: any) {
      setMapGeoHint(e?.message || 'Could not determine your device location. Click the map to drop a pin.');
    } finally {
      setMapLocating(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setStatusMsg(null);
    try {
      const result: AddressValidateResult = await api.addresses.validate({
        address_line1: value.address_line1,
        address_line2: value.address_line2,
        city: value.city,
        state: value.state,
        zip_code: value.zip_code,
      });
      if (!result?.available || result.verdict === 'unavailable') {
        setStatusMsg(result?.message || 'Address validation unavailable. You can still save manually.');
        setStatusSeverity('warning');
        return;
      }
      const next = mergeAddress(value, {
        address_line1: result.address?.address_line1 ?? value.address_line1,
        address_line2: result.address?.address_line2 ?? value.address_line2,
        city: result.address?.city ?? value.city,
        state: result.address?.state ?? value.state,
        zip_code: result.address?.zip_code ?? value.zip_code,
        latitude: result.address?.latitude ?? value.latitude,
        longitude: result.address?.longitude ?? value.longitude,
      });
      onChange(next);
      if (result.verdict === 'fix') {
        setStatusMsg(result.message || 'Please fix the address.');
        setStatusSeverity('error');
      } else if (result.verdict === 'confirm') {
        setStatusMsg(result.message || 'Please confirm the suggested address.');
        setStatusSeverity('warning');
      } else {
        setStatusMsg(result.raw_formatted || 'Address looks valid.');
        setStatusSeverity('success');
      }
    } catch (e: any) {
      setStatusMsg(e?.message || 'Validation failed. You can still enter the address manually.');
      setStatusSeverity('warning');
    } finally {
      setValidating(false);
    }
  };

  const stateValue =
    isValidUsStateAbbrev(normalizeUsState(value.state)) && normalizeUsState(value.state)
      ? normalizeUsState(value.state)
      : '';

  const req = required ? ' *' : '';
  const line1Missing = required && showErrors && !value.address_line1.trim();
  const cityMissing = required && showErrors && !value.city.trim();
  const stateMissing = required && showErrors && !stateValue;
  const zipMissing = required && showErrors && !value.zip_code.trim();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={`Address Line 1${req}`}
            value={value.address_line1}
            onChange={e => handleLine1Change(e.target.value)}
            onKeyDown={handleLine1KeyDown}
            onBlur={() => closeSuggestions()}
            disabled={disabled}
            inputRef={line1Ref}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            name="street-address-line-1"
            autoComplete="off"
            error={line1Missing}
            helperText={
              line1Missing
                ? 'Address is required'
                : helperText ||
                  (mapsConfigured && mapsReady
                    ? showCurrentLocation
                      ? 'Start typing for Google suggestions, use current location, or pick on the map.'
                      : 'Start typing for suggestions, or pick on the map.'
                    : undefined)
            }
          />
          {suggestionsOpen && suggestions.length > 0 && (
            <Paper
              variant="outlined"
              sx={{ mt: 0.5, maxHeight: 220, overflow: 'auto' }}
            >
              {suggestions.map((suggestion, index) => (
                <Box
                  key={suggestion.place_id}
                  onMouseDown={event => {
                    event.preventDefault();
                    selectSuggestion(suggestion.place_id);
                  }}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: index === activeSuggestion ? 'action.hover' : 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2">{suggestion.description}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2"
            value={value.address_line2}
            onChange={e => onChange(mergeAddress(value, { address_line2: e.target.value }))}
            disabled={disabled}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            autoComplete="address-line2"
          />
        </Grid>
        <Grid item xs={12} sm={afterCity ? 4 : 4}>
          <TextField
            fullWidth
            label={`City${req}`}
            value={value.city}
            onChange={e => onChange(mergeAddress(value, { city: e.target.value }))}
            disabled={disabled}
            error={cityMissing}
            helperText={cityMissing ? 'City is required' : undefined}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            autoComplete="address-level2"
          />
        </Grid>
        {afterCity}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={disabled} error={stateMissing}>
            <InputLabel id="address-fields-state-label">{`State${req}`}</InputLabel>
            <Select
              labelId="address-fields-state-label"
              label={`State${req}`}
              value={stateValue}
              onChange={e => onChange(mergeAddress(value, { state: String(e.target.value) }))}
              MenuProps={{
                // Keep menu inside the dialog stacking context (avoids portal under nested Dialog)
                disablePortal: true,
                disableScrollLock: true,
              }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {US_STATE_ABBREVIATIONS.map(abbr => (
                <MenuItem key={abbr} value={abbr}>
                  {abbr}
                </MenuItem>
              ))}
            </Select>
            {stateMissing && <FormHelperText>State is required</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={`ZIP${req}`}
            value={value.zip_code}
            onChange={e => onChange(mergeAddress(value, { zip_code: e.target.value }))}
            disabled={disabled}
            error={zipMissing}
            helperText={zipMissing ? 'ZIP is required' : undefined}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            autoComplete="postal-code"
          />
        </Grid>
        {(showMapPicker || showValidate || showCurrentLocation) && (
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {showCurrentLocation && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={locating ? <CircularProgress size={16} /> : <MyLocationIcon />}
                  disabled={disabled || locating}
                  onClick={handleUseCurrentLocation}
                >
                  My current location
                </Button>
              )}
              {showMapPicker && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MapIcon />}
                  disabled={disabled || !mapsConfigured || !mapsReady}
                  onClick={() => setMapOpen(true)}
                >
                  Pick on map
                </Button>
              )}
              {showValidate && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={validating ? <CircularProgress size={16} /> : <VerifiedOutlinedIcon />}
                  disabled={disabled || validating}
                  onClick={handleValidate}
                >
                  Validate address
                </Button>
              )}
            </Stack>
            {mapsError && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Map features unavailable ({mapsError}). Enter the address manually.
              </Typography>
            )}
            {!mapsConfigured && showMapPicker && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Map picker is not configured. Enter the address manually.
              </Typography>
            )}
          </Grid>
        )}
        {statusMsg && (
          <Grid item xs={12}>
            <Alert severity={statusSeverity} onClose={() => setStatusMsg(null)}>
              {statusMsg}
            </Alert>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={mapOpen}
        onClose={() => !mapBusy && setMapOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>Pick location on map</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The map centers on your device when location is allowed. Click or drag the marker, then confirm.
          </Typography>
          {mapGeoHint && (
            <Alert severity={pendingPin ? 'info' : 'warning'} sx={{ mb: 1 }}>
              {mapGeoHint}
            </Alert>
          )}
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={mapContainerRef}
              sx={{ width: '100%', height: 360, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}
            />
            {mapLocating && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.6)',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRecenterMapOnDevice}
            disabled={mapBusy || mapLocating}
            startIcon={mapLocating ? <CircularProgress size={16} /> : <MyLocationIcon />}
          >
            My location
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setMapOpen(false)} disabled={mapBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUsePin} disabled={mapBusy || !pendingPin}>
            {mapBusy ? <CircularProgress size={22} color="inherit" /> : 'Use this location'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddressFields;
